/**
 * Effective-DPI thresholds for user photos at their rendered frame size.
 * Single source of truth for the final-check validator (`dataParser.js`),
 * the placeholder badge (`photoQuality.js`), the preview alert
 * (`photoQualityAlert.js`) and the Polotno canvas overlay
 * (`polotno/image/imageElement.js`).
 *
 * `MIN` is the default warning line and can be overridden per product by
 * `template.minDPI` (see `getPhotoDpiThresholds`), because forgiving media
 * such as canvas tolerate less than glossy paper. `CRITICAL` and `GOOD`
 * stay global: below `CRITICAL` the print is unusable on any medium, above
 * `GOOD` no medium benefits from more pixels enough to nag the user.
 *
 * `template.bestDPI` is intentionally not part of this: the template
 * generator uses it to size server assets, so it must stay at 300 even
 * where the UI would never ask a phone photo for 300 DPI.
 */
export const PHOTO_DPI = Object.freeze({
  CRITICAL: 36,
  MIN: 70,
  GOOD: 150,
});

/**
 * Resolves the thresholds for a product template.
 *
 * @param {object} [template] - Product template (`{ template: { minDPI } }`).
 * @returns {{ critical: number, min: number, good: number }}
 */
export const getPhotoDpiThresholds = (template) => {
  const templateMin = template?.template?.minDPI;
  const min =
    typeof templateMin === 'number' && templateMin > PHOTO_DPI.CRITICAL
      ? templateMin
      : PHOTO_DPI.MIN;

  return {
    critical: PHOTO_DPI.CRITICAL,
    min,
    good: Math.max(PHOTO_DPI.GOOD, min),
  };
};

/**
 * Effective DPI of a photo placed in a frame, cover-fit and cropped.
 * The limiting axis wins. A user zoom (`scale` > 1) spreads fewer source
 * pixels over the same frame, so it divides the result. A quarter-turn
 * rotation (`orientation` odd) swaps which photo edge meets which frame edge.
 *
 * @param {{ width: number, height: number }} photo - Source pixel size.
 * @param {{ width: number, height: number }} frame - Frame size in cm.
 * @param {{ scale?: number, orientation?: number }} [placement]
 * @returns {number}
 */
export const getEffectiveDpi = (photo, frame, placement = {}) => {
  const scale = placement.scale > 0 ? placement.scale : 1;
  const rotated = (placement.orientation ?? 0) % 2 === 1;
  const frameWidthIn = (rotated ? frame.height : frame.width) / 2.54;
  const frameHeightIn = (rotated ? frame.width : frame.height) / 2.54;

  return Math.min(photo.width / (frameWidthIn * scale), photo.height / (frameHeightIn * scale));
};
